<template>
  <div
    class="filesystem-explorer"
    :class="{ 'filesystem-explorer-embedded': embedded }"
  >
    <div v-if="!splitView" class="explorer-header">
      <div>
        <h2><i :class="titleIcon"></i> {{ title }}</h2>
        <p v-if="description" class="explorer-description">
          {{ description }}
        </p>
      </div>
      <button
        v-if="!splitView"
        @click="fetchTree"
        :disabled="loading"
        class="refresh-btn"
      >
        <i class="fas fa-sync-alt"></i>
        {{ loading ? "Caricamento..." : "Aggiorna" }}
      </button>
    </div>

    <!-- Stato caricamento / errore -->
    <div v-if="loading" class="status-message loading">
      <i class="fas fa-spinner fa-spin"></i> Caricamento struttura file...
    </div>
    <div v-else-if="error" class="status-message error">
      <i class="fas fa-exclamation-triangle"></i> {{ error }}
    </div>

    <div
      v-else-if="splitView"
      class="split-explorer-layout"
      :class="{ 'split-explorer-layout--users': userNavigation }"
    >
      <aside
        class="split-tree-panel"
        :class="{ 'split-user-panel': userNavigation }"
        :aria-label="
          userNavigation ? 'Utenti dello studio' : 'Struttura archivio'
        "
      >
        <div class="split-panel-heading">
          <i :class="userNavigation ? 'fa-solid fa-users' : titleIcon"></i>
          <span>{{ userNavigation ? "Utenti dello studio" : title }}</span>
        </div>
        <div v-if="userNavigation" class="split-user-list">
          <button
            v-for="userNode in fixedUserNodes"
            :key="userNode.rel_path"
            type="button"
            class="split-user-item"
            :class="{ active: isUserNodeActive(userNode) }"
            :aria-current="isUserNodeActive(userNode) ? 'page' : null"
            @click="selectUserNode(userNode)"
          >
            <i class="fa-solid fa-user" aria-hidden="true"></i>
            <span>{{ resolveDisplayName(userNode) }}</span>
          </button>
        </div>
        <ul v-else class="tree-root split-tree-root">
          <TreeNode
            v-for="node in folderTree"
            :key="node.rel_path"
            :node="node"
            :depth="0"
            :selected-path="selectedFolderPath"
            :label-resolver="resolveDisplayName"
            :show-empty-message="false"
            toggle-on-folder-click
            @select-folder="handleSelectFolder"
            @toggle-folder="handleToggleFolder"
            @folder-context-menu="openFolderContextMenu"
          />
        </ul>
      </aside>

      <section class="split-content-panel" aria-label="Contenuto cartella">
        <nav class="folder-breadcrumb" aria-label="Percorso cartella">
          <button
            v-if="userNavigation"
            type="button"
            class="breadcrumb-home"
            title="Torna all'elenco utenti"
            aria-label="Torna all'elenco utenti"
            @click="clearFolderSelection"
          >
            <i class="fa-solid fa-house" aria-hidden="true"></i>
          </button>
          <i v-else class="fa-solid fa-house" aria-hidden="true"></i>
          <template v-for="folder in selectedFolderBreadcrumb">
            <i
              :key="`${folder.rel_path}-separator`"
              class="fa-solid fa-chevron-right breadcrumb-separator"
              aria-hidden="true"
            ></i>
            <button
              :key="folder.rel_path"
              type="button"
              :class="{
                current: folder.rel_path === selectedFolderPath,
              }"
              @click="selectBreadcrumbFolder(folder)"
            >
              {{ resolveDisplayName(folder) }}
            </button>
          </template>
        </nav>

        <div class="folder-content-toolbar">
          <label class="folder-file-search">
            <i class="fa-solid fa-magnifying-glass"></i>
            <input
              v-model.trim="fileSearchQuery"
              type="search"
              :disabled="!selectedFolderPath"
              placeholder="Cerca in questa cartella..."
              aria-label="Cerca nella cartella selezionata"
            />
          </label>
          <button @click="fetchTree" :disabled="loading" class="refresh-btn">
            <i class="fas fa-sync-alt"></i>
            {{ loading ? "Caricamento..." : "Aggiorna" }}
          </button>
        </div>

        <div class="folder-content-card">
          <div class="split-panel-heading folder-content-heading">
            <span>
              <i class="fa-solid fa-folder"></i>
              Contenuto cartella
            </span>
            <small>{{ selectedFolderContentSummary }}</small>
          </div>

          <div v-if="!selectedFolderPath" class="folder-empty-state">
            <i class="fa-solid fa-users"></i>
            Seleziona un utente per visualizzarne librerie e file.
          </div>
          <div
            v-else-if="!hasFilteredSelectedFolderContent"
            class="folder-empty-state"
          >
            <i class="fa-regular fa-folder-open"></i>
            {{
              fileSearchQuery
                ? "Nessun elemento corrisponde alla ricerca."
                : "Nessun elemento presente in questa cartella."
            }}
          </div>
          <div v-else class="folder-content-body">
            <div
              v-if="filteredSelectedFolderFolders.length"
              class="folder-directory-grid"
              aria-label="Cartelle"
            >
              <button
                v-for="folder in filteredSelectedFolderFolders"
                :key="folder.rel_path"
                type="button"
                class="folder-directory-item"
                :title="resolveDisplayName(folder)"
                @click="navigateToFolder(folder)"
              >
                <i class="fa-solid fa-folder" aria-hidden="true"></i>
                <span
                  v-if="
                    resolveDisplayName(folder) ===
                    'File personalizzati per clienti'
                  "
                >
                  File personalizzati<br />per clienti
                </span>
                <span v-else>{{ resolveDisplayName(folder) }}</span>
              </button>
            </div>
            <div
              v-if="filteredSelectedFolderFiles.length"
              class="folder-file-list"
              role="table"
            >
              <div class="folder-file-list-header" role="row">
                <span role="columnheader">Nome file</span>
                <span role="columnheader">Dimensione</span>
                <span role="columnheader">Ultima modifica</span>
                <span aria-hidden="true"></span>
              </div>
              <div
                v-for="file in filteredSelectedFolderFiles"
                :key="file.rel_path"
                class="folder-file-row"
                role="row"
              >
                <button
                  type="button"
                  class="folder-file-name"
                  role="cell"
                  :title="resolveDisplayName(file)"
                  @click="handleOpenFile(file.rel_path)"
                >
                  <i :class="fileIconClass(file)"></i>
                  <span>{{ resolveDisplayName(file) }}</span>
                </button>
                <span class="folder-file-size" role="cell">
                  {{ formatFileSize(file.size_bytes) }}
                </span>
                <span class="folder-file-date" role="cell">
                  {{ file.last_modified || "—" }}
                </span>
                <button
                  type="button"
                  class="folder-file-action"
                  title="Apri file"
                  aria-label="Apri file"
                  @click="handleOpenFile(file.rel_path)"
                >
                  <i class="fa-solid fa-ellipsis-vertical"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Albero -->
    <ul v-else class="tree-root">
      <TreeNode
        v-for="node in tree"
        :key="node.rel_path"
        :node="node"
        :depth="0"
        :selected-path="selectedFolderPath"
        :label-resolver="resolveDisplayName"
        @open-file="handleOpenFile"
        @select-folder="handleSelectFolder"
        @toggle-folder="handleToggleFolder"
        @folder-context-menu="openFolderContextMenu"
      />
    </ul>

    <div
      v-if="contextMenu.visible"
      class="context-menu-overlay"
      @click="closeContextMenu"
    >
      <div
        class="context-menu"
        :style="{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }"
        @click.stop
      >
        <button
          class="context-menu-item danger"
          @click="requestDeleteSelectedFolder"
        >
          <i class="fa-solid fa-trash"></i> Elimina
        </button>
      </div>
    </div>

    <!-- Modal preview file -->
    <div v-if="previewVisible" class="file-preview-modal" @click="closePreview">
      <div class="modal-content" @click.stop>
        <button class="close-btn" @click="closePreview">×</button>
        <h3 class="preview-title">{{ previewFileName }}</h3>

        <!-- Toolbar -->
        <div class="preview-toolbar">
          <button
            @click="downloadCurrentFile"
            class="btn-action btn-download"
            title="Scarica il file"
            :disabled="isReplacing || isDeleting"
          >
            <i class="fas fa-download"></i> Scarica
          </button>

          <button
            @click="replaceCurrentFile"
            class="btn-action btn-replace"
            title="Sostituisci questo file"
            :disabled="isReplacing || isDeleting"
          >
            <i class="fa-solid fa-upload"></i> Sostituisci
          </button>

          <button
            @click="deleteCurrentFile"
            class="btn-action btn-delete"
            title="Elimina definitivamente"
            :disabled="isReplacing || isDeleting"
          >
            <i class="fa-solid fa-trash"></i> Elimina
          </button>

          <div class="flex-spacer"></div>
        </div>

        <!-- Contenuto preview -->
        <div v-if="isExcel" class="excel-preview">
          <table class="excel-table-preview">
            <tr v-for="(row, i) in previewJson" :key="i">
              <td v-for="(cell, j) in row" :key="j">{{ cell ?? "" }}</td>
            </tr>
          </table>
        </div>

        <iframe
          v-else-if="isViewable"
          :src="previewUrl"
          class="file-iframe"
          title="Anteprima file"
        ></iframe>

        <div v-else class="download-prompt">
          <i class="fas fa-file-alt fa-4x"></i>
          <p>File non visualizzabile direttamente nel browser.</p>
          <button @click="downloadCurrentFile" class="btn-download">
            <i class="fas fa-download"></i> Scarica file
          </button>
        </div>

        <!-- Overlay loading operazioni -->
        <div v-if="isReplacing || isDeleting" class="preview-loading-overlay">
          <div class="loading-spinner">
            <i class="fas fa-spinner fa-spin fa-3x"></i>
            <p>
              {{
                isReplacing
                  ? "Sostituzione in corso..."
                  : "Eliminazione in corso..."
              }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal conferma eliminazione -->
    <div
      v-if="confirmDeleteVisible"
      class="confirm-delete-overlay"
      @click="cancelDelete"
    >
      <div class="confirm-delete-box" @click.stop>
        <h4>Conferma eliminazione</h4>
        <p>Stai per eliminare definitivamente:</p>
        <p class="file-name-to-delete">{{ fileToDeleteName }}</p>
        <p v-if="deleteTargetType === 'folder'" class="warning-text">
          La cartella e tutto il suo contenuto verranno eliminati
          definitivamente.
        </p>
        <div v-if="deleteTargetType === 'folder'" class="delete-password-block">
          <label class="delete-password-label" for="delete-folder-password">
            Inserisci la password di conferma
          </label>
          <input
            id="delete-folder-password"
            v-model="deletePassword"
            type="password"
            class="delete-password-input"
            placeholder="Password richiesta"
            :disabled="isDeleting"
            @keydown.enter.prevent="proceedDelete"
          />
        </div>
        <p v-else class="warning-text">Questa azione è irreversibile.</p>

        <p v-if="deleteModalMessage" class="delete-modal-message">
          {{ deleteModalMessage }}
        </p>
        <div class="confirm-actions">
          <button
            class="btn-confirm-cancel"
            @click="cancelDelete"
            :disabled="isDeleting"
          >
            Annulla
          </button>
          <button
            class="btn-confirm-delete"
            @click="proceedDelete"
            :disabled="isDeleting"
          >
            <span v-if="isDeleting">
              <i class="fas fa-spinner fa-spin"></i> Eliminazione...
            </span>
            <span v-else>Elimina definitivamente</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_BASE_URL } from "@/config.js";
import TreeNode from "@/components/TreeNode.vue";
import * as XLSX from "xlsx";

export default {
  name: "FileExplorer",
  components: { TreeNode },
  props: {
    scope: {
      type: String,
      default: "global",
    },
    title: {
      type: String,
      default: "Esplora File",
    },
    titleIcon: {
      type: String,
      default: "fa-solid fa-folder-tree",
    },
    description: {
      type: String,
      default: "",
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    rootLabel: {
      type: String,
      default: "",
    },
    labels: {
      type: Object,
      default: () => ({}),
    },
    includePaths: {
      type: Array,
      default: () => [],
    },
    startPath: {
      type: String,
      default: "",
    },
    showStartPathNode: {
      type: Boolean,
      default: false,
    },
    defaultOpenDepth: {
      type: Number,
      default: 0,
    },
    permissionErrorMessage: {
      type: String,
      default: "",
    },
    allowFolderDelete: {
      type: Boolean,
      default: true,
    },
    splitView: {
      type: Boolean,
      default: false,
    },
    userNavigation: {
      type: Boolean,
      default: false,
    },
    treeTransformer: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      tree: [],
      loading: false,
      error: null,

      // Preview
      previewVisible: false,
      previewFileName: "",
      previewUrl: "",
      previewJson: [],
      isExcel: false,
      currentRelPath: "",
      fileBlob: null,
      selectedFolderPath: "",
      selectedFolderName: "",
      fileSearchQuery: "",

      // Operazioni
      isReplacing: false,
      isDeleting: false,
      confirmDeleteVisible: false,
      fileToDeleteName: "",
      deleteTargetPath: "",
      deleteTargetType: "file",
      deletePassword: "",
      deleteModalMessage: "",
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
      },
    };
  },

  computed: {
    normalizedIncludePaths() {
      return this.includePaths.map(this.normalizeFilterPath).filter(Boolean);
    },
    normalizedStartPath() {
      return this.normalizeFilterPath(this.startPath);
    },
    folderTree() {
      return this.onlyFolderNodes(this.tree);
    },
    fixedUserNodes() {
      if (!this.userNavigation) return [];

      const rootNode = this.normalizedStartPath
        ? this.findNodeByRelPath(this.tree, this.normalizedStartPath)
        : null;
      const candidates = rootNode?.children || this.tree;

      return (candidates || []).filter((node) => node.type === "folder");
    },
    selectedFolderNode() {
      if (!this.selectedFolderPath) return null;
      return this.findNodeByRelPath(this.tree, this.selectedFolderPath);
    },
    selectedFolderFiles() {
      if (!this.selectedFolderNode) return [];
      return (this.selectedFolderNode.children || []).filter(
        (node) => node.type !== "folder"
      );
    },
    selectedFolderFolders() {
      if (!this.selectedFolderNode) return [];
      return (this.selectedFolderNode.children || []).filter(
        (node) => node.type === "folder"
      );
    },
    filteredSelectedFolderFolders() {
      const query = this.fileSearchQuery.toLocaleLowerCase("it");
      if (!query) return this.selectedFolderFolders;
      return this.selectedFolderFolders.filter((folder) =>
        this.resolveDisplayName(folder).toLocaleLowerCase("it").includes(query)
      );
    },
    filteredSelectedFolderFiles() {
      const query = this.fileSearchQuery.toLocaleLowerCase("it");
      if (!query) return this.selectedFolderFiles;
      return this.selectedFolderFiles.filter((file) =>
        this.resolveDisplayName(file).toLocaleLowerCase("it").includes(query)
      );
    },
    hasFilteredSelectedFolderContent() {
      return Boolean(
        this.filteredSelectedFolderFolders.length ||
          this.filteredSelectedFolderFiles.length
      );
    },
    selectedFolderContentSummary() {
      if (!this.selectedFolderPath) return "—";

      const folderCount = this.filteredSelectedFolderFolders.length;
      const fileCount = this.filteredSelectedFolderFiles.length;
      return `${folderCount} ${
        folderCount === 1 ? "cartella" : "cartelle"
      } · ${fileCount} file`;
    },
    selectedFolderBreadcrumb() {
      if (!this.selectedFolderPath) return [];
      const trail =
        this.findNodeTrail(this.tree, this.selectedFolderPath) || [];
      if (!this.userNavigation) return trail;

      return trail.filter(
        (node) =>
          this.normalizeFilterPath(node.rel_path) !== this.normalizedStartPath
      );
    },
    isViewable() {
      if (!this.currentRelPath) return false;
      const ext = this.currentRelPath.split(".").pop()?.toLowerCase() || "";
      return [
        "pdf",
        "txt",
        "csv",
        "jpg",
        "jpeg",
        "png",
        "gif",
        "webp",
      ].includes(ext);
    },
  },

  mounted() {
    this.fetchTree();
    document.addEventListener("keydown", this.handleGlobalKeydown);
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this.handleGlobalKeydown);
  },

  methods: {
    normalizeFilterPath(path) {
      return String(path || "")
        .replace(/\\/g, "/")
        .replace(/^\/+|\/+$/g, "");
    },

    onlyFolderNodes(nodes) {
      return (nodes || [])
        .filter((node) => node.type === "folder")
        .map((node) => ({
          ...node,
          children: this.onlyFolderNodes(node.children || []),
        }));
    },

    shouldIncludeNode(node) {
      if (!this.normalizedIncludePaths.length) return true;

      const relPath = this.normalizeFilterPath(node?.rel_path);
      if (!relPath) return true;

      return this.normalizedIncludePaths.some(
        (includedPath) =>
          relPath === includedPath ||
          relPath.startsWith(`${includedPath}/`) ||
          includedPath.startsWith(`${relPath}/`)
      );
    },

    filterTree(nodes) {
      if (!this.normalizedIncludePaths.length) return nodes;

      return nodes
        .map((node) => {
          const children = node.children ? this.filterTree(node.children) : [];
          if (!this.shouldIncludeNode(node) && !children.length) return null;

          return {
            ...node,
            ...(node.children ? { children } : {}),
          };
        })
        .filter(Boolean);
    },

    applyStartPath(nodes) {
      if (!this.normalizedStartPath) return nodes;

      const startNode = this.findNodeByRelPath(nodes, this.normalizedStartPath);

      if (!startNode) return [];
      return this.showStartPathNode ? [startNode] : startNode.children || [];
    },

    sortTreeByDisplayName(nodes) {
      return [...nodes]
        .map((node) => ({
          ...node,
          ...(node.children
            ? { children: this.sortTreeByDisplayName(node.children) }
            : {}),
        }))
        .sort((left, right) => {
          const leftOrder = Number.isFinite(left.sort_order)
            ? left.sort_order
            : Number.POSITIVE_INFINITY;
          const rightOrder = Number.isFinite(right.sort_order)
            ? right.sort_order
            : Number.POSITIVE_INFINITY;

          if (leftOrder !== rightOrder) {
            return leftOrder - rightOrder;
          }

          if (left.type !== right.type) {
            return left.type === "folder" ? -1 : 1;
          }

          return this.resolveDisplayName(left).localeCompare(
            this.resolveDisplayName(right),
            "it",
            { sensitivity: "base" }
          );
        });
    },

    resolveDisplayName(node) {
      if (!node) return "";
      if (!node.rel_path && this.rootLabel) return this.rootLabel;
      return (
        node.display_name ||
        this.labels[node.rel_path] ||
        this.labels[node.name] ||
        node.name
      );
    },

    buildFileUrl(path) {
      const params = new URLSearchParams({
        path,
        scope: this.scope,
      });
      return `${API_BASE_URL}/get_file_from_file_system?${params.toString()}`;
    },

    async fetchTree() {
      this.loading = true;
      this.error = null;

      try {
        const { data } = await axios.get(`${API_BASE_URL}/get_filesystem`, {
          params: { scope: this.scope },
        });

        if (!data.success) {
          throw new Error(data.error || "Errore dal server");
        }

        const visibleTree = this.applyStartPath(
          this.filterTree(data.tree || [])
        );
        const transformedTree = this.treeTransformer
          ? this.treeTransformer(visibleTree)
          : visibleTree;

        this.tree = this.sortTreeByDisplayName(transformedTree || []);
        this.setOpenState(this.tree, false);
        this.setOpenStateToDepth(this.tree, this.defaultOpenDepth);
        this.restoreSelectedFolder();
        if (this.splitView && !this.userNavigation) {
          this.ensureSplitSelection();
        }
      } catch (err) {
        console.error("Errore fetchTree:", err);

        if (err.response?.status === 401 || err.response?.status === 422) {
          this.error = "Sessione scaduta. Effettua nuovamente il login.";
        } else if (err.response?.status === 403) {
          this.error =
            this.permissionErrorMessage ||
            "Non hai i permessi per visualizzare questi file.";
        } else {
          this.error =
            err.response?.data?.error || "Impossibile caricare la struttura.";
        }
      } finally {
        this.loading = false;
      }
    },

    setOpenState(nodes, open = false) {
      nodes.forEach((node) => {
        if (node.type === "folder") {
          this.$set(node, "open", open);
          if (node.children) this.setOpenState(node.children, open);
        }
      });
    },

    setOpenStateToDepth(nodes, depth) {
      if (!depth || depth < 1) return;

      nodes.forEach((node) => {
        if (node.type !== "folder") return;
        this.$set(node, "open", true);

        if (node.children) {
          this.setOpenStateToDepth(node.children, depth - 1);
        }
      });
    },

    async handleOpenFile(relPath) {
      this.closeContextMenu();
      if (!this.splitView) {
        this.selectedFolderPath = "";
        this.selectedFolderName = "";
      }
      this.currentRelPath = relPath;
      this.previewFileName = relPath.split("/").pop();
      this.isExcel = /\.(xlsx|xls)$/i.test(relPath);

      try {
        const config = {
          responseType: this.isExcel ? "arraybuffer" : "blob",
          params: {
            path: relPath,
            scope: this.scope,
          },
        };

        const response = await axios.get(
          `${API_BASE_URL}/get_file_from_file_system`,
          config
        );

        if (this.isExcel) {
          const data = new Uint8Array(response.data);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          this.previewJson = XLSX.utils.sheet_to_json(firstSheet, {
            header: 1,
          });

          const maxCols = Math.max(
            ...this.previewJson.map((r) => r.length || 0)
          );
          this.previewJson.forEach((row) => {
            while (row.length < maxCols) row.push("");
          });
        } else {
          this.fileBlob = response.data;
          this.previewUrl = URL.createObjectURL(this.fileBlob);
        }

        this.previewVisible = true;
      } catch (err) {
        console.error("Errore apertura file:", err);
        let msg = "Impossibile aprire il file.";
        if (err.response) {
          if ([401, 422].includes(err.response.status)) {
            msg = "Sessione scaduta. Effettua nuovamente il login.";
          } else if (err.response.status === 403) {
            msg = "Non autorizzato a visualizzare questo file.";
          } else if (err.response.status === 404) {
            msg = "File non trovato sul server.";
          } else {
            msg = err.response.data?.error || msg;
          }
        }
        alert(msg);
        this.closePreview();
      }
    },

    downloadCurrentFile() {
      if (this.isExcel) {
        const url = this.buildFileUrl(this.currentRelPath);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", this.previewFileName);
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (this.fileBlob) {
        const url = URL.createObjectURL(this.fileBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = this.previewFileName;
        a.click();
        URL.revokeObjectURL(url);
      }
    },

    async replaceCurrentFile() {
      if (!this.currentRelPath) {
        alert("Nessun file selezionato");
        return;
      }

      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls,.csv,.pdf,.docx,.txt,.jpg,.jpeg,.png";

      input.onchange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 25 * 1024 * 1024) {
          alert("Il file è troppo grande (max 25 MB)");
          return;
        }

        if (
          !confirm(
            `Sostituire "${this.previewFileName}" con "${file.name}"?\nIl file originale verrà sovrascritto.`
          )
        ) {
          return;
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("path", this.currentRelPath);
        formData.append("scope", this.scope);

        this.isReplacing = true;

        try {
          const response = await axios.post(
            `${API_BASE_URL}/filesystem_replace_file`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );

          if (response.data.success) {
            alert("File sostituito con successo!");
            this.closePreview();
            await this.fetchTree();
          } else {
            alert(response.data.error || "Errore durante la sostituzione");
          }
        } catch (err) {
          console.error("Errore replace:", err);
          alert(
            err.response?.data?.error ||
              "Errore durante la sostituzione del file"
          );
        } finally {
          this.isReplacing = false;
        }
      };

      input.click();
    },

    deleteCurrentFile() {
      if (!this.currentRelPath) return;
      this.deleteTargetPath = this.currentRelPath;
      this.deleteTargetType = "file";
      this.deletePassword = "";
      this.deleteModalMessage = "";
      this.fileToDeleteName = this.previewFileName;
      this.confirmDeleteVisible = true;
    },

    handleSelectFolder(node) {
      this.selectedFolderPath = node.rel_path;
      this.selectedFolderName = this.resolveDisplayName(node);
      this.fileSearchQuery = "";
      this.closeContextMenu();
    },

    selectUserNode(node) {
      this.handleSelectFolder(node);
    },

    navigateToFolder(node) {
      this.handleSelectFolder(node);
      this.openFolderTrail(node.rel_path);
    },

    clearFolderSelection() {
      this.selectedFolderPath = "";
      this.selectedFolderName = "";
      this.fileSearchQuery = "";
      this.closeContextMenu();
    },

    isUserNodeActive(node) {
      const userPath = this.normalizeFilterPath(node?.rel_path);
      const selectedPath = this.normalizeFilterPath(this.selectedFolderPath);
      return Boolean(
        userPath &&
          (selectedPath === userPath || selectedPath.startsWith(`${userPath}/`))
      );
    },

    handleToggleFolder(node) {
      const sourceNode =
        this.findNodeByRelPath(this.tree, node.rel_path) || node;
      this.$set(sourceNode, "open", !sourceNode.open);
      this.handleSelectFolder(sourceNode);
    },

    openFolderContextMenu({ node, x, y }) {
      if (!this.allowFolderDelete) return;
      this.selectedFolderPath = node.rel_path;
      this.selectedFolderName = this.resolveDisplayName(node);
      this.contextMenu = {
        visible: true,
        x,
        y,
      };
    },

    closeContextMenu() {
      this.contextMenu.visible = false;
    },

    requestDeleteSelectedFolder() {
      if (!this.allowFolderDelete || !this.selectedFolderPath) return;
      this.deleteTargetPath = this.selectedFolderPath;
      this.deleteTargetType = "folder";
      this.fileToDeleteName = this.selectedFolderName;
      this.deletePassword = "";
      this.deleteModalMessage = "";
      this.confirmDeleteVisible = true;
      this.closeContextMenu();
    },

    handleGlobalKeydown(event) {
      if (event.key !== "Delete") return;
      if (this.previewVisible || this.confirmDeleteVisible || this.isDeleting) {
        return;
      }

      const tagName = event.target?.tagName?.toLowerCase();
      if (
        ["input", "textarea", "select"].includes(tagName) ||
        event.target?.isContentEditable
      ) {
        return;
      }

      if (this.allowFolderDelete && this.selectedFolderPath) {
        event.preventDefault();
        this.requestDeleteSelectedFolder();
      }
    },

    findNodeByRelPath(nodes, relPath) {
      for (const node of nodes) {
        if (node.rel_path === relPath) return node;
        if (node.children?.length) {
          const found = this.findNodeByRelPath(node.children, relPath);
          if (found) return found;
        }
      }
      return null;
    },

    findNodeTrail(nodes, relPath, trail = []) {
      for (const node of nodes || []) {
        if (node.type !== "folder") continue;
        const nextTrail = [...trail, node];
        if (node.rel_path === relPath) return nextTrail;
        const found = this.findNodeTrail(node.children, relPath, nextTrail);
        if (found) return found;
      }
      return null;
    },

    findFirstFolderWithFiles(nodes) {
      for (const node of nodes || []) {
        if (node.type !== "folder") continue;
        if ((node.children || []).some((child) => child.type !== "folder")) {
          return node;
        }
        const nested = this.findFirstFolderWithFiles(node.children);
        if (nested) return nested;
      }
      return null;
    },

    openFolderTrail(relPath) {
      const trail = this.findNodeTrail(this.tree, relPath) || [];
      trail.forEach((node) => this.$set(node, "open", true));
    },

    ensureSplitSelection() {
      let selected = this.selectedFolderNode;
      if (!selected || selected.type !== "folder") {
        selected = this.tree.find((node) => node.type === "folder") || null;
      }
      if (!selected) return;
      this.selectedFolderPath = selected.rel_path;
      this.selectedFolderName = this.resolveDisplayName(selected);
      this.openFolderTrail(selected.rel_path);
    },

    selectBreadcrumbFolder(folder) {
      this.handleSelectFolder(folder);
      this.openFolderTrail(folder.rel_path);
    },

    fileExtension(node) {
      const name = String(node?.name || "");
      return name.includes(".") ? name.split(".").pop().toLowerCase() : "";
    },

    fileIconClass(node) {
      const icons = {
        csv: "fa-solid fa-file-csv",
        xls: "fa-solid fa-file-excel",
        xlsx: "fa-solid fa-file-excel",
        pdf: "fa-solid fa-file-pdf",
        doc: "fa-solid fa-file-word",
        docx: "fa-solid fa-file-word",
        txt: "fa-solid fa-file-lines",
        zip: "fa-solid fa-file-zipper",
      };
      return icons[this.fileExtension(node)] || "fa-regular fa-file";
    },

    formatFileSize(sizeInBytes) {
      const bytes = Number(sizeInBytes);
      if (!Number.isFinite(bytes) || bytes < 0) return "—";
      if (bytes === 0) return "0 B";

      const units = ["B", "KB", "MB", "GB"];
      const unitIndex = Math.min(
        Math.floor(Math.log(bytes) / Math.log(1024)),
        units.length - 1
      );
      const value = bytes / 1024 ** unitIndex;
      const formattedValue = new Intl.NumberFormat("it-IT", {
        maximumFractionDigits: unitIndex > 0 && value < 10 ? 1 : 0,
      }).format(value);

      return `${formattedValue} ${units[unitIndex]}`;
    },

    restoreSelectedFolder() {
      if (!this.selectedFolderPath) return;

      const selectedNode = this.findNodeByRelPath(
        this.tree,
        this.selectedFolderPath
      );

      if (!selectedNode || selectedNode.type !== "folder") {
        this.selectedFolderPath = "";
        this.selectedFolderName = "";
        return;
      }

      this.selectedFolderName = selectedNode.name;
    },

    async proceedDelete() {
      if (this.deleteTargetType === "folder" && !this.deletePassword.trim()) {
        this.deleteModalMessage =
          "Inserisci la password per eliminare la cartella.";
        return;
      }

      this.deleteModalMessage = "";
      this.isDeleting = true;
      let shouldCloseModal = false;

      try {
        const response = await axios.delete(
          `${API_BASE_URL}/filesystem_delete_file?path=${encodeURIComponent(
            this.deleteTargetPath
          )}&scope=${encodeURIComponent(this.scope)}`,
          {
            data:
              this.deleteTargetType === "folder"
                ? { password: this.deletePassword, scope: this.scope }
                : { scope: this.scope },
          }
        );

        if (response.data.success) {
          shouldCloseModal = true;
          alert(
            this.deleteTargetType === "folder"
              ? "Cartella eliminata con successo"
              : "File eliminato con successo"
          );

          if (this.deleteTargetType === "file") {
            this.closePreview();
          } else {
            this.selectedFolderPath = "";
            this.selectedFolderName = "";
          }

          await this.fetchTree();
        } else {
          const errorMessage =
            response.data.error || "Errore durante l'eliminazione";
          if (this.deleteTargetType === "folder") {
            this.deleteModalMessage = errorMessage;
            return;
          }
          alert(errorMessage);
        }
      } catch (err) {
        console.error("Errore delete:", err);
        const errorMessage =
          err.response?.data?.error ||
          "Errore di rete durante l'eliminazione del file";
        if (this.deleteTargetType === "folder") {
          this.deleteModalMessage = errorMessage;
          return;
        }
        alert(errorMessage);
      } finally {
        this.isDeleting = false;
        if (shouldCloseModal) {
          this.confirmDeleteVisible = false;
          this.fileToDeleteName = "";
          this.deleteTargetPath = "";
          this.deleteTargetType = "file";
          this.deletePassword = "";
          this.deleteModalMessage = "";
        }
      }
    },

    cancelDelete() {
      this.confirmDeleteVisible = false;
      this.fileToDeleteName = "";
      this.deleteTargetPath = "";
      this.deleteTargetType = "file";
      this.deletePassword = "";
      this.deleteModalMessage = "";
      this.isDeleting = false;
      this.closeContextMenu();
    },

    closePreview() {
      this.previewVisible = false;
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = "";
      }
      this.previewJson = [];
      this.fileBlob = null;
      this.currentRelPath = "";
      this.isReplacing = false;
      this.isDeleting = false;
      this.deleteModalMessage = "";
      this.closeContextMenu();
    },
  },
};
</script>

<style scoped>
/* Stili esistenti invariati + aggiunte per modal conferma e loading */

.filesystem-explorer {
  font-family: "Segoe UI", system-ui, sans-serif;
  box-sizing: border-box;
  background: #f8f9fa;
  min-height: 70vh;
  width: calc(100% - 40px);
  max-width: 1220px;
  display: flex;
  flex-direction: column;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  margin: 20px;
}

.filesystem-explorer-embedded {
  width: 100%;
  max-width: none;
  box-sizing: border-box;
  height: 100%;
  min-height: 0;
  margin: 0;
  background: #ffffff;
  border-color: #dfe6ee;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.08);
}

.explorer-header {
  background: #fff;
  padding: 14px 18px;
  border-bottom: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.explorer-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  text-align: left;
}

.explorer-header h2 i {
  margin-right: 8px;
  color: #34495e;
}

.explorer-description {
  margin: 4px 0 0;
  color: #607083;
  font-size: 13px;
  line-height: 1.35;
}

.refresh-btn {
  background: #3498db;
  color: white;
  border: none;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.refresh-btn:hover:not(:disabled) {
  background: #2980b9;
}

.status-message {
  padding: 30px;
  text-align: center;
  font-size: 15px;
}

.loading {
  color: #3498db;
}
.error {
  color: #e74c3c;
}

.tree-root {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
}

.split-explorer-layout {
  display: grid;
  flex: 1 1 0;
  grid-template-columns: minmax(21rem, 25rem) minmax(0, 1fr);
  min-width: 0;
  min-height: 0;
  gap: 0.85rem;
  padding: 0.85rem;
  box-sizing: border-box;
  overflow: hidden;
  background: #f7f9fb;
}

.split-explorer-layout--users {
  grid-template-columns: minmax(11.5rem, 13.5rem) minmax(0, 1fr);
}

.split-tree-panel,
.split-content-panel,
.folder-content-card {
  min-width: 0;
  min-height: 0;
  border: 1px solid #dfe6ee;
  border-radius: 0.7rem;
  background: #fff;
  overflow: hidden;
}

.split-tree-panel,
.split-content-panel {
  display: flex;
  flex-direction: column;
}

.split-panel-heading {
  display: flex;
  min-height: 2.8rem;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 0.8rem;
  box-sizing: border-box;
  border-bottom: 1px solid #e3e9ef;
  background: linear-gradient(180deg, #fbfcfd 0%, #f4f7f9 100%);
  color: #253c59;
  font-size: 0.82rem;
  font-weight: 700;
}

.split-panel-heading > i,
.folder-content-heading i {
  color: #07865e;
}

.split-tree-root {
  padding: 0.55rem 0.45rem 0.75rem;
  overflow-x: hidden;
}

.split-tree-root ::v-deep .node-header {
  min-height: 1.9rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  border-radius: 0.35rem;
}

.split-tree-root ::v-deep .node-header.selected {
  outline: 0;
  background: #dff3e9;
}

.split-tree-root ::v-deep .node-icon {
  color: #07865e;
}

.split-tree-root ::v-deep .node-name {
  color: #2b405a;
  font-size: 0.78rem;
  line-height: 1.25;
  overflow: visible;
  overflow-wrap: anywhere;
  text-overflow: clip;
  white-space: normal;
}

.split-user-list {
  display: flex;
  flex: 1 1 0;
  min-height: 0;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.45rem;
  overflow-x: hidden;
  overflow-y: auto;
}

.split-user-item {
  display: flex;
  width: 100%;
  min-height: 2.55rem;
  align-items: center;
  gap: 0.6rem;
  box-sizing: border-box;
  padding: 0.45rem 0.6rem;
  overflow: hidden;
  border: 0;
  border-left: 3px solid transparent;
  border-radius: 0.45rem;
  background: transparent;
  color: #344b64;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 650;
  text-align: left;
  cursor: pointer;
  transition: color 0.18s ease, background 0.18s ease, border-color 0.18s ease;
}

.split-user-item i {
  width: 1.05rem;
  flex: 0 0 1.05rem;
  color: #58718a;
  text-align: center;
}

.split-user-item span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.split-user-item:hover {
  background: #f2f7f5;
  color: #087552;
}

.split-user-item.active {
  border-left-color: #07815e;
  background: #e4f4ec;
  color: #087552;
}

.split-user-item.active i {
  color: #07815e;
}

.split-content-panel {
  border-radius: 0.7rem;
}

.folder-breadcrumb {
  display: flex;
  min-width: 0;
  min-height: 2.8rem;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 0.8rem;
  box-sizing: border-box;
  border-bottom: 1px solid #e3e9ef;
  background: linear-gradient(180deg, #fbfcfd 0%, #f5f7fa 100%);
  color: #5e728b;
}

.folder-breadcrumb > i:first-child {
  color: #405976;
}

.breadcrumb-home {
  display: inline-flex;
  width: 1.7rem;
  height: 1.7rem;
  flex: 0 0 1.7rem;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 0.35rem;
  background: transparent;
  color: #405976;
  cursor: pointer;
}

.breadcrumb-home:hover {
  background: #e7f3ed;
  color: #087a55;
}

.folder-breadcrumb button {
  min-width: 0;
  max-width: 13rem;
  padding: 0;
  overflow: hidden;
  border: 0;
  background: transparent;
  color: #60758f;
  font: inherit;
  font-size: 0.72rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.folder-breadcrumb button:hover,
.folder-breadcrumb button.current {
  color: #087a55;
}

.folder-breadcrumb button.current {
  font-weight: 700;
}

.breadcrumb-separator {
  color: #a1adba;
  font-size: 0.55rem;
}

.folder-content-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.65rem;
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid #edf1f5;
}

.folder-file-search {
  display: flex;
  min-width: 0;
  min-height: 2.35rem;
  align-items: center;
  gap: 0.65rem;
  padding: 0 0.75rem;
  box-sizing: border-box;
  border: 1px solid #d5e0eb;
  border-radius: 0.45rem;
  background: #fff;
  color: #73869d;
}

.folder-file-search input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #243c5a;
  font: inherit;
  font-size: 0.78rem;
}

.folder-content-toolbar .refresh-btn {
  min-height: 2.35rem;
  padding: 0.5rem 0.85rem;
  background: #0b8660;
  font-size: 0.76rem;
  font-weight: 650;
}

.folder-content-toolbar .refresh-btn:hover:not(:disabled) {
  background: #076f50;
}

.folder-content-card {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  margin: 0 0.75rem 0.75rem;
}

.folder-content-heading {
  justify-content: space-between;
}

.folder-content-heading > span {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.folder-content-heading small {
  color: #7a8ba0;
  font-size: 0.7rem;
  font-weight: 600;
}

.folder-content-body {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.folder-directory-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(9rem, 10.5rem));
  align-items: start;
  gap: 0.75rem;
  padding: 0.9rem;
  border-bottom: 1px solid #edf1f4;
}

.folder-directory-item {
  display: flex;
  min-width: 0;
  min-height: 6.25rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.6rem;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid transparent;
  border-radius: 0.6rem;
  background: transparent;
  color: #29405e;
  font: inherit;
  font-size: 0.76rem;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease,
    box-shadow 0.18s ease;
}

.folder-directory-item i {
  color: #e4aa18;
  font-size: 2.9rem;
  filter: drop-shadow(0 3px 4px rgba(142, 102, 0, 0.12));
}

.folder-directory-item > span {
  width: 100%;
  min-width: 0;
  min-height: 1.9rem;
  display: block;
  overflow: visible;
  line-height: 1.25;
  overflow-wrap: anywhere;
  text-overflow: clip;
  white-space: normal;
}

.folder-directory-item:hover,
.folder-directory-item:focus-visible {
  border-color: #cadce8;
  outline: 0;
  background: #f3f8fb;
  box-shadow: 0 3px 10px rgba(40, 71, 94, 0.08);
}

.folder-content-body .folder-file-list {
  flex: none;
  overflow: visible;
}

.folder-file-list {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.folder-file-list-header,
.folder-file-row {
  display: grid;
  grid-template-columns:
    minmax(0, 1.55fr) minmax(0, 0.55fr) minmax(0, 0.65fr)
    2rem;
  min-width: 0;
  align-items: center;
  column-gap: 0.5rem;
}

.folder-file-list-header {
  min-height: 2rem;
  padding: 0 0.65rem;
  border-bottom: 1px solid #e2e8ef;
  background: #f1f4f7;
  color: #667a92;
  font-size: 0.66rem;
  font-weight: 650;
}

.folder-file-row {
  min-height: 2.75rem;
  padding: 0.25rem 0.65rem;
  box-sizing: border-box;
  border-bottom: 1px solid #edf1f4;
  color: #566b83;
  font-size: 0.72rem;
}

.folder-file-row:last-child {
  border-bottom: 0;
}

.folder-file-row:hover {
  background: #f8fbfa;
}

.folder-file-name {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.7rem;
  padding: 0;
  overflow: hidden;
  border: 0;
  background: transparent;
  color: #29405e;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.folder-file-name i {
  width: 1rem;
  flex: 0 0 1rem;
  color: #07865e;
  font-size: 1rem;
  text-align: center;
}

.folder-file-name span,
.folder-file-size,
.folder-file-date {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.folder-file-action {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #58708d;
  cursor: pointer;
}

.folder-file-action:hover {
  background: #e7f3ed;
  color: #087a55;
}

.folder-empty-state {
  display: flex;
  flex: 1 1 0;
  min-height: 8rem;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 1.5rem;
  color: #75879b;
  font-size: 0.78rem;
  text-align: center;
}

.folder-empty-state i {
  color: #2f8f72;
}

@media (max-width: 900px) {
  .split-explorer-layout {
    grid-template-columns: minmax(0, 1fr);
    overflow-y: auto;
  }

  .split-tree-panel {
    max-height: 14rem;
  }

  .split-content-panel {
    min-height: 20rem;
  }

  .split-explorer-layout--users {
    grid-template-columns: minmax(0, 1fr);
  }

  .split-user-panel {
    max-height: 13rem;
  }
}

.context-menu-overlay {
  position: fixed;
  inset: 0;
  z-index: 9500;
}

.context-menu {
  position: fixed;
  min-width: 150px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.16);
  padding: 6px;
}

.context-menu-item {
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #2c3e50;
}

.context-menu-item:hover {
  background: #f4f7fa;
}

.context-menu-item.danger {
  color: #c0392b;
}

/* MODAL PREVIEW */
.file-preview-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  width: 95%;
  max-width: 1300px;
  height: 92vh;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  position: relative;
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 16px;
  width: 40px;
  height: 40px;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 26px;
  cursor: pointer;
  z-index: 10;
}

.preview-title {
  margin: 0;
  padding: 16px 20px;
  background: #f1f1f1;
  border-bottom: 1px solid #ddd;
  font-size: 16px;
  font-weight: 500;
}

.preview-toolbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  background: #ffffff;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.btn-action {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1.1rem;
  font-size: 0.94rem;
  font-weight: 500;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.22s ease;
  background: transparent;
  color: #444;
}

.btn-action:hover {
  background: #f3f4f6;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.btn-action:active {
  transform: translateY(0);
  background: #e5e7eb;
}

.flex-spacer {
  flex: 1;
}

/* Contenuti preview */
.excel-preview {
  flex: 1;
  overflow: auto;
  padding: 10px;
  background: #fff;
}

.excel-table-preview {
  border-collapse: collapse;
  font-size: 13px;
  width: 100%;
}

.excel-table-preview td {
  border: 1px solid #ccc;
  padding: 6px 10px;
  min-width: 80px;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.excel-table-preview tr:nth-child(odd) {
  background: #f9f9f9;
}

.file-iframe {
  flex: 1;
  border: none;
  width: 100%;
}

.download-prompt {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  color: #555;
  font-size: 1.3rem;
}

/* Loading overlay */
.preview-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.loading-spinner {
  text-align: center;
  color: #3498db;
}

.loading-spinner p {
  margin-top: 12px;
  font-weight: 500;
}

/* Modal conferma delete */
.confirm-delete-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.confirm-delete-box {
  background: white;
  padding: 24px;
  border-radius: 12px;
  width: 420px;
  max-width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  text-align: center;
}

.confirm-delete-box h4 {
  margin: 0 0 16px;
  color: #b91c1c;
}

.file-name-to-delete {
  font-weight: bold;
  margin: 12px 0;
  word-break: break-all;
  color: #333;
}

.warning-text {
  color: #991b1b;
  font-size: 0.95rem;
  margin: 16px 0 24px;
}

.delete-password-block {
  margin: -8px 0 20px;
  text-align: left;
}

.delete-password-label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.92rem;
  font-weight: 600;
  color: #374151;
}

.delete-password-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.95rem;
  box-sizing: border-box;
}

.delete-password-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.14);
}

.delete-modal-message {
  margin: 0 0 20px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 0.92rem;
  font-weight: 600;
  text-align: left;
}

.confirm-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.btn-confirm-cancel {
  padding: 10px 24px;
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.btn-confirm-cancel:hover {
  background: #4b5563;
}

.btn-confirm-delete {
  padding: 10px 24px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
}

.btn-confirm-delete:hover:not(:disabled) {
  background: #b91c1c;
}

.btn-confirm-delete:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
